import clsx from 'clsx';
import { useIntl, type MessageDescriptor } from 'react-intl';
import type {
  GroupBase,
  NoticeProps as ReactNoOptionsMessageProps,
} from 'react-select';

import { canReceiveDefaultTextFormat as _canReceiveDefaultTextFormat } from '../../../core/utils/can-receive-default-text-format.js';
import { Text } from '../../../typography/text/Text.js';
import * as styles from '../styles/Select.sty.js';

// Shared message descriptor for the "no results" state and the corresponding screen-reader announcement.
export const noResultsMessage: MessageDescriptor = {
  defaultMessage: 'No results found',
  id: 'U09aNQxYT1yrJ4eJ',
  description: 'Default message when no options are available in the select.',
};

type NoOptionsMessageProps<
  OptionValue,
  isMulti extends boolean,
  Group extends GroupBase<OptionValue>,
> = ReactNoOptionsMessageProps<OptionValue, isMulti, Group>;

export function NoOptionsMessage<
  OptionValue,
  isMulti extends boolean,
  Group extends GroupBase<OptionValue>,
>(props: Readonly<NoOptionsMessageProps<OptionValue, isMulti, Group>>) {
  const selectEmptyState = props.selectProps.emptyState?.props.children;
  const {
    className: consumerClassName,
    style: consumerStyle,
    ...remainingProps
  } = props.selectProps.componentProps.emptyState ?? {};
  const intl = useIntl();

  if (!selectEmptyState || _canReceiveDefaultTextFormat(selectEmptyState)) {
    return (
      <Text
        textStyle="base"
        style={consumerStyle}
        className={clsx(styles.selectNoOptionsMessage, consumerClassName)}
        as="p"
        {...remainingProps}
      >
        {selectEmptyState ?? intl.formatMessage(noResultsMessage)}
      </Text>
    );
  } else {
    return (
      <Text
        textStyle="base"
        style={consumerStyle}
        className={clsx(styles.selectNoOptionsMessage, consumerClassName)}
        as="p"
        {...remainingProps}
      >
        {selectEmptyState}
      </Text>
    );
  }
}
